import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { cloneDeep } from '@apollo/client/utilities';

import { KybDocument, KybDocumentType } from '__generated__/GQL';
import Button from 'components/_core/Button';
import FileUpload from 'components/_core/FileUpload';
import IconButton from 'components/_core/IconButton';
import Modal from 'components/_core/Modal';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineDropdown from 'components/_core/SunshineDropdown';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import Text from 'components/_core/Text';
import { FILE_SIZE, MEGABYTE } from 'components/Upload';
import { getUploadKybFunction } from 'features/Storage/upload';

import { ApplicationFollowUpData } from '../ApplicationFollowUp';
import { locales } from '../applicationSteps/locales';
import { DeleteKybDocDocument } from '../graphql/mutations/deleteKybDoc.gql';
import { UploadKybDocDocument } from '../graphql/mutations/uploadKybDocument.gql';
import { ListKybDocumentsDocument } from '../graphql/query/listKybDocuments.gql';

interface UploadKybDocumentModalProps {
  documentType: null | KybDocumentType.Kbis | KybDocumentType.Statutes;
  onClose: () => void;
  data: ApplicationFollowUpData;
  existingDocument: Pick<
    KybDocument,
    'status' | 'type' | 'kybDocumentId' | 'url'
  > | null;
}

export const UploadKybDocumentModal = ({
  data,
  documentType,
  existingDocument,
  onClose,
}: UploadKybDocumentModalProps) => {
  const { companyProfileId } = data.companyProfile;

  const [uploadKybDoc, kybDocUpload] = useMutation(UploadKybDocDocument);
  const [deleteKybDoc, kybDocDeletion] = useMutation(DeleteKybDocDocument);

  const [isUploading, setIsUploading] = useState(false);
  const isClosable =
    !isUploading && !kybDocUpload.loading && !kybDocDeletion.loading;

  return (
    <Modal
      isOpen={!!documentType}
      onClose={() => {
        if (isClosable) {
          onClose();
        }
      }}
    >
      <Modal.Header />

      {documentType ? (
        <Modal.Body>
          <Modal.Title marginBottom="space-16">
            {locales.applicationRegistration[documentType].modalTitle}
          </Modal.Title>

          <Text marginBottom="space-24">
            {locales.applicationRegistration.modalBody}
          </Text>

          {existingDocument ? (
            <SunshineCard alignItems="center">
              <SunshineCard.Slot name="aside">
                <SquircleIcon name="file" />
              </SunshineCard.Slot>

              <SunshineCard.Content>
                <SunshineCard.Text
                  fontSize="font-15"
                  textAlign="left"
                  variant="primary"
                >
                  {locales.applicationRegistration.file}
                </SunshineCard.Text>
              </SunshineCard.Content>

              <SunshineCard.Slot name="action">
                <SunshineDropdown>
                  <SunshineDropdown.Trigger>
                    <IconButton
                      aria-label="menu"
                      icon="vertical-ellipsis"
                      isLoading={kybDocDeletion.loading}
                      variant="secondary"
                    />
                  </SunshineDropdown.Trigger>

                  <SunshineDropdown.List>
                    <SunshineDropdown.Item
                      icon="eye-open"
                      isDisabled={!existingDocument.url}
                      onClick={() => {
                        if (existingDocument.url) {
                          window.open(existingDocument.url, '_blank');
                        }
                      }}
                    >
                      {locales.applicationRegistration.seeDocument}
                    </SunshineDropdown.Item>

                    <SunshineDropdown.Item
                      colorScheme="red"
                      icon="trash"
                      onClick={() => {
                        deleteKybDoc({
                          update(proxy, result) {
                            const cache = cloneDeep(
                              proxy.readQuery({
                                query: ListKybDocumentsDocument,
                                variables: { companyProfileId },
                              }),
                            );

                            const deletedDocId =
                              result.data?.deleteKybDocument.kybDocument
                                .kybDocumentId;

                            if (cache && deletedDocId) {
                              cache.viewer.company.kybDocuments =
                                cache.viewer.company.kybDocuments.filter(
                                  ({ kybDocumentId }) =>
                                    kybDocumentId !== deletedDocId,
                                );
                            }

                            proxy.writeQuery({
                              data: cache,
                              query: ListKybDocumentsDocument,
                              variables: { companyProfileId },
                            });
                          },

                          variables: {
                            input: {
                              kybDocumentId: existingDocument.kybDocumentId,
                            },
                          },
                        });
                      }}
                    >
                      {locales.applicationRegistration.deleteDocument}
                    </SunshineDropdown.Item>
                  </SunshineDropdown.List>
                </SunshineDropdown>
              </SunshineCard.Slot>
            </SunshineCard>
          ) : (
            <FileUpload
              maxByteSizeParam={FILE_SIZE.SUPPORTING_DOCUMENTS}
              onSuccessfulUpload={async ({ bucketName, fileRef }) => {
                await uploadKybDoc({
                  update(proxy, result) {
                    const cache = cloneDeep(
                      proxy.readQuery({
                        query: ListKybDocumentsDocument,
                        variables: { companyProfileId },
                      }),
                    );

                    if (cache && result.data) {
                      cache.viewer.company.kybDocuments =
                        cache.viewer.company.kybDocuments.filter(
                          (d) => d.type !== documentType,
                        );

                      cache.viewer.company.kybDocuments.push(
                        result.data.addOrReplaceKybDocument.kybDocument,
                      );
                    }

                    proxy.writeQuery({
                      data: cache,
                      query: ListKybDocumentsDocument,
                      variables: { companyProfileId },
                    });
                  },

                  variables: {
                    input: {
                      companyProfileId,
                      firebaseBucket: bucketName,
                      firebaseRef: fileRef,
                      type: documentType,
                      uid: data.userProfile.uid,
                    },
                  },
                });
              }}
              onUploadEnd={() => setIsUploading(false)}
              onUploadStart={() => setIsUploading(true)}
              subtitle={locales.formatString(
                locales.applicationRegistration.modalUploadSubtitle,
                {
                  sizeInMo: Math.round(
                    (FILE_SIZE.SUPPORTING_DOCUMENTS['application/pdf'] ??
                      FILE_SIZE.SUPPORTING_DOCUMENTS.default) / MEGABYTE,
                  ),
                },
              )}
              title={
                documentType === KybDocumentType.Kbis
                  ? locales.applicationRegistration.modalUploadTitleKbis
                  : locales.applicationRegistration.modalUploadTitleStatutes
              }
              uploadFile={getUploadKybFunction(
                data.companyProfile.companyProfileId,
              )}
            />
          )}
        </Modal.Body>
      ) : null}
      <Modal.Footer justifyContent="flex-end">
        <Button isDisabled={!existingDocument || !isClosable} onClick={onClose}>
          Continuer
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
